import React, { useState } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { clamp, propsToDom } from 'core/helpers';
import { Container } from 'components/Container';
import { Panel } from 'components/Panel';
import linkResolver from 'core/linkResolver';
import { Image } from 'components/Image';
import { RichText } from 'prismic-reactjs';
import { h2 } from 'styles/typography';
import { rem } from 'styles/utils';
import Swiper from 'components/Swiper';
import media from 'styles/media';
import { Reveal } from 'components/Reveal';

const TestimonialsComponent = ({ id, primary, items, slice_type, next_slice_type, ...others }) => {
  const filtered = items.filter(({ quote }) => quote?.richText?.length > 0);
  const [active, setActive] = useState(0);

  const onSwipe = ({ deltaX, deltaY }) => {
    if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 100) {
      const next = clamp(active + Math.sign(deltaX), 0, filtered.length - 1);
      setActive(next);
    }
  };

  return (
    <Panel
      {...propsToDom(others)}
      id={slice_type}
      nextId={next_slice_type}
      background={primary.background}
    >
      <Swiper tag={Container} enableMouse={true} onSwipe={onSwipe}>
        <Items style={{ '--active': active }}>
          {filtered.map(({ image, quote, author }, index) => {
            const hasImage = !!image?.url;
            const hasAuthor = !!author;

            return (
              <Testimonial
                key={`quote-${index}`}
                isActive={active === index}
                onClick={() => setActive(index)}
              >
                {hasImage && <Image {...image} />}
                <Reveal stagger={index}>
                  <Blockquote>
                    {RichText.render(quote.richText, linkResolver)}
                    {hasAuthor && <cite>{author}</cite>}
                  </Blockquote>
                </Reveal>
              </Testimonial>
            );
          })}
        </Items>
      </Swiper>
    </Panel>
  );
};

const Items = styled.div`
  display: flex;
  transition: transform 0.75s ${({ theme }) => theme.easings.principle};
  user-select: none;
  will-change: transform;
  transform: translateX(calc(${rem(672 + 384)} * var(--active) * -1));

  ${media.mobile`
    transform: translateX(calc(${rem(274 + 55)} * var(--active) * -1));
  `}
`;

const Testimonial = styled.div`
  flex-shrink: 0;
  width: ${rem(672)};
  opacity: ${({ isActive }) => (isActive ? 1 : 0.1)};
  transition: opacity 0.75s ${({ theme }) => theme.easings.principle};

  &:not(:last-child) {
    margin-right: ${rem(384)};
  }

  ${Image} {
    width: ${rem(144)};
    margin-bottom: ${rem(48)};
  }

  ${media.mobile`
    width: ${rem(274)};

    &:not(:last-child) {
      margin-right: ${rem(55)};
    }
  `}
`;

const Blockquote = styled.blockquote`
  p {
    ${h2}
  }

  cite {
    display: block;
    font-style: normal;
    margin-top: ${rem(24)};
    color: ${({ theme }) => theme.colors.font};
  }
`;

export const Testimonials = styled(TestimonialsComponent)`
  ${Container} {
    position: relative;
    overflow: hidden;
  }
`;

export const query = graphql`
  fragment SliceTestimonialsHome on PrismicHomeDataBodyTestimonials {
    id
    slice_type
    primary {
      background
    }
    items {
      image {
        url
        alt
        dimensions {
          width
          height
        }
      }
      quote {
        richText
      }
      author
    }
  }

  fragment SliceTestimonialsPage on PrismicPageDataBodyTestimonials {
    id
    slice_type
    primary {
      background
    }
    items {
      image {
        url
        alt
        dimensions {
          width
          height
        }
      }
      quote {
        richText
      }
      author
    }
  }
`;
